import React, { forwardRef, useCallback, useId } from "react";

import { Slider as PrimitiveSlider } from "src/primitives/Slider";
import { cn } from "src/shadcn/lib/utils";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";

type SliderValue = number | number[];

type PrimitiveSliderProps = React.ComponentProps<typeof PrimitiveSlider>;

export interface SliderProps extends Omit<
  PrimitiveSliderProps,
  "value" | "defaultValue" | "onValueChange" | "onValueCommit"
> {
  /** Current value. Pass a number for a single-thumb slider or an array for a range slider. */
  value?: SliderValue;
  /** Default value (uncontrolled). Shape mirrors `value`. */
  defaultValue?: SliderValue;
  /** Called on every change. Emits a number if `value`/`defaultValue` was a number, an array otherwise. */
  onValueChange?: (value: SliderValue) => void;
  /** Called when the user stops dragging. Emits a number if `value`/`defaultValue` was a number, an array otherwise. */
  onValueCommit?: (value: SliderValue) => void;
  /** Label displayed above the slider. */
  label?: string;
  /** Error message displayed below the slider. */
  error?: string;
  /** Help text displayed below the slider. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Shows a required indicator next to the label. */
  required?: boolean;
  /** Additional class name for the outermost wrapper. */
  className?: string;
  /** Props forwarded to the FieldLabel element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
}

const toArray = (value: SliderValue | undefined): number[] | undefined => {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value : [value];
};

const Slider = forwardRef<
  React.ComponentRef<typeof PrimitiveSlider>,
  SliderProps
>(
  (
    {
      label,
      error,
      helpText,
      required = false,
      className,
      labelProps,
      disabled,
      id: idProp,
      value,
      defaultValue,
      onValueChange,
      onValueCommit,
      ...otherProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const errorId = `error_${id}`;
    const helpTextId = `helpText_${id}`;

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    const hasField = !!(label || error || helpText);

    const inputShapeIsScalar =
      value !== undefined
        ? !Array.isArray(value)
        : defaultValue !== undefined
          ? !Array.isArray(defaultValue)
          : false;

    const unwrap = useCallback(
      (next: number[]): SliderValue =>
        inputShapeIsScalar ? (next[0] as number) : next,
      [inputShapeIsScalar]
    );

    const handleValueChange = useCallback(
      (next: number[]) => onValueChange?.(unwrap(next)),
      [onValueChange, unwrap]
    );

    const handleValueCommit = useCallback(
      (next: number[]) => onValueCommit?.(unwrap(next)),
      [onValueCommit, unwrap]
    );

    const slider = (
      <PrimitiveSlider
        ref={ref}
        id={id}
        disabled={disabled}
        aria-invalid={!!error || undefined}
        aria-describedby={ariaDescribedBy}
        aria-label={!label ? undefined : label}
        className={cn(!hasField && className)}
        value={toArray(value)}
        defaultValue={toArray(defaultValue)}
        onValueChange={onValueChange ? handleValueChange : undefined}
        onValueCommit={onValueCommit ? handleValueCommit : undefined}
        {...otherProps}
      />
    );

    if (!hasField) {
      return slider;
    }

    return (
      <Field
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
        className={className}
      >
        {label && (
          <FieldLabel htmlFor={id} {...labelProps}>
            {label}
            {required && (
              <span aria-hidden="true" className="text-destructive">
                *
              </span>
            )}
          </FieldLabel>
        )}
        <FieldContent>
          {slider}
          {!!error && <FieldError id={errorId}>{error}</FieldError>}
          {helpText && (
            <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
          )}
        </FieldContent>
      </Field>
    );
  }
);

Slider.displayName = "Slider";

export { Slider };
